import { ApiError } from "@khazzin/api-client";
import { Button, ConfirmDialog, Select } from "@khazzin/ui/primitives";
import { useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";

/**
 * What a store may do to its own `pending` order (EP10-S05): Edit, the circle
 * withdraw, and Cancel. The server refuses each once the warehouse has started
 * (`409 order_not_pending`); these are drawn only while it has not.
 */

const REASONS = [
	"CUSTOMER_REMORSE",
	"WRONG_ITEM",
	"DAMAGED",
	"NOT_AS_DESCRIBED",
	"FAILED_DELIVERY",
	"OTHER",
] as const;

function editPath(orderId: string): string {
	return `/orders/${encodeURIComponent(orderId)}/edit`;
}

/** Edit while pending, or Resubmit once withdrawn: the same form, at the same address. */
export function EditOrderLink({
	orderId,
	resubmit = false,
}: {
	orderId: string;
	resubmit?: boolean;
}) {
	const { t } = useLocale();
	return (
		<Link
			to={editPath(orderId)}
			className="text-[12.5px] font-semibold text-brand-ink underline-offset-2 hover:underline"
		>
			{t(resubmit ? "portal.orders.actions.resubmit" : "portal.orders.actions.edit")}
		</Link>
	);
}

/** The circle: confirm, withdraw, and open the order in the form with Resubmit. */
export function WithdrawButton({
	orderId,
	orderNo,
	storeId,
}: {
	orderId: string;
	orderNo: string;
	storeId: string;
}) {
	const { t } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [failed, setFailed] = useState<string | null>(null);
	return (
		<>
			<Button
				type="button"
				variant="secondary"
				size="sm"
				className="size-12 rounded-full p-0 md:size-8"
				aria-label={t("portal.orders.actions.withdraw", { orderNo })}
				title={t("portal.orders.actions.withdraw", { orderNo })}
				onClick={(event) => {
					// A row click opens the order; the circle is its own action.
					event.stopPropagation();
					setFailed(null);
					setOpen(true);
				}}
			>
				<RotateCcw aria-hidden className="size-4" />
			</Button>
			<ConfirmDialog
				open={open}
				onOpenChange={setOpen}
				title={t("portal.orders.withdraw.title", { orderNo })}
				description={t("portal.orders.withdraw.body")}
				confirmLabel={t("portal.orders.withdraw.confirm")}
				cancelLabel={t("portal.orders.withdraw.keep")}
				closeLabel={t("ui.actions.close")}
				onConfirm={async () => {
					try {
						await api.withdrawPortalOrder(orderId);
					} catch (error) {
						setFailed(
							error instanceof ApiError ? error.message : t("portal.orders.actions.failed"),
						);
						throw error;
					}
					await queries.invalidateQueries({ queryKey: ["portal", storeId, "orders"] });
					void navigate(editPath(orderId));
				}}
			>
				{failed !== null && (
					<p role="alert" className="text-[12.5px] text-danger-fg">
						{failed}
					</p>
				)}
			</ConfirmDialog>
		</>
	);
}

/** Cancel while pending: nothing has been picked, so nothing is charged. */
export function CancelOrderButton({
	orderId,
	orderNo,
	version,
	storeId,
}: {
	orderId: string;
	orderNo: string;
	version: number;
	storeId: string;
}) {
	const { t } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const [open, setOpen] = useState(false);
	const [reason, setReason] = useState<(typeof REASONS)[number]>("CUSTOMER_REMORSE");
	const [failed, setFailed] = useState<string | null>(null);
	return (
		<>
			<Button
				type="button"
				variant="ghost"
				size="sm"
				className="min-h-12 md:min-h-0"
				onClick={() => {
					setFailed(null);
					setOpen(true);
				}}
			>
				{t("portal.orders.actions.cancel")}
			</Button>
			<ConfirmDialog
				open={open}
				onOpenChange={setOpen}
				tone="danger"
				title={t("portal.orders.cancel.title", { orderNo })}
				description={t("portal.orders.cancel.body")}
				confirmLabel={t("portal.orders.cancel.confirm")}
				cancelLabel={t("portal.orders.cancel.keep")}
				closeLabel={t("ui.actions.close")}
				onConfirm={async () => {
					try {
						await api.cancelPortalOrder(orderId, { reasonCode: reason }, version);
					} catch (error) {
						setFailed(
							error instanceof ApiError ? error.message : t("portal.orders.actions.failed"),
						);
						throw error;
					}
					await queries.invalidateQueries({ queryKey: ["portal", storeId, "orders"] });
				}}
			>
				<Select
					aria-label={t("portal.orders.cancel.reason")}
					value={reason}
					onValueChange={(next) => setReason(next as (typeof REASONS)[number])}
					options={REASONS.map((value) => ({
						value,
						label: t(`portal.orders.cancel.reasons.${value}`),
					}))}
				/>
				{failed !== null && (
					<p role="alert" className="text-[12.5px] text-danger-fg">
						{failed}
					</p>
				)}
			</ConfirmDialog>
		</>
	);
}
